import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Save, X, RefreshCw } from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { useToast } from '../common/Toast';
import MaintenanceService from '../../services/maintenanceService';
import { assetService } from '../../services/assetService';

const initialFormState = {
  assetId: '',
  type: 'Preventive',
  description: '',
  scheduledDate: new Date().toISOString().split('T')[0],
  completedDate: '',
  status: 'Scheduled',
  priority: 'Medium',
  cost: 0,
  vendor: '',
  technician: '',
  notes: '',
  location: '',
  estimatedDuration: ''
};

const NewMaintenance = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location || {};
  const stateFormData = state?.formData;
  const scheduledDateFromState = state?.scheduledDate;

  const { success, error: showError } = useToast();

  const [assets, setAssets] = useState([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState(initialFormState);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadAssets();
  }, []);

  useEffect(() => {
    if (stateFormData && typeof stateFormData === 'object') {
      setFormData({ ...initialFormState, ...stateFormData });
      return;
    }
    if (scheduledDateFromState) {
      setFormData(prev => ({ ...prev, scheduledDate: scheduledDateFromState }));
    }
  }, [stateFormData, scheduledDateFromState]);

  const loadAssets = async () => {
    try {
      setLoadingAssets(true);
      const response = await assetService.getAssets({ status: 'Active', limit: 100 });

      let assetsArray = [];
      if (response?.success && response?.data?.assets) {
        assetsArray = response.data.assets;
      } else if (response?.data && Array.isArray(response.data)) {
        assetsArray = response.data;
      } else if (Array.isArray(response)) {
        assetsArray = response;
      } else if (response?.assets && Array.isArray(response.assets)) {
        assetsArray = response.assets;
      }

      if (assetsArray.length > 0) {
        const transformedAssets = assetsArray.map(asset => ({
          assetId: asset.assetCode || asset.assetId || asset._id?.toString() || asset.id?.toString() || '',
          assetCode: asset.assetCode || asset.assetId || '',
          _id: asset._id?.toString() || asset.id?.toString() || '',
          name: asset.name || 'Unnamed Asset',
          assetName: asset.name || 'Unnamed Asset',
          type: asset.type || 'Other'
        }));
        setAssets(transformedAssets);
      } else {
        setAssets([]);
      }
    } catch (err) {
      console.error('Error loading assets:', err);
      setAssets([]);
    } finally {
      setLoadingAssets(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      if (!formData.assetId) {
        throw new Error('Asset selection is required');
      }
      if (!formData.description || !formData.description.trim()) {
        throw new Error('Description is required');
      }
      if (!formData.scheduledDate) {
        throw new Error('Scheduled Date is required');
      }

      const apiData = {
        ...formData,
        maintenanceId: formData.assetId ? `${formData.assetId}-MNT-${Date.now()}` : `MNT-${Date.now()}`,
        technician: formData.technician ? { name: formData.technician, contact: '' } : null,
        vendor: formData.vendor ? { name: formData.vendor, contact: '', phone: '' } : null,
        cost: parseFloat(formData.cost) || 0
      };

      if (formData.assetId) {
        apiData.asset = {
          assetId: formData.assetId,
          assetName: `Asset ${formData.assetId}`,
          assetType: 'Equipment',
          location: formData.location || 'Unknown Location'
        };
      }
      delete apiData.assetId;

      const response = await MaintenanceService.createMaintenance(apiData);
      if (response && response.success) {
        success('Maintenance record created successfully!');
        navigate('/assets/maintenance');
      } else {
        throw new Error(response?.message || 'Failed to create maintenance record');
      }
    } catch (err) {
      console.error('Error creating maintenance record:', err);
      const message = err?.message || 'Failed to create maintenance record';
      setError(message);
      showError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/assets/maintenance');
  };

  const headerActions = [
    {
      label: 'Back to Maintenance',
      icon: <X size={18} />,
      onClick: handleCancel,
      variant: 'outline',
      tooltip: 'Return to maintenance list'
    }
  ];

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title="Create Maintenance Record"
        description="Create a new maintenance record for assets"
        actions={headerActions}
      />

      <div className="page-container" style={{ maxWidth: '100%' }}>
        {error && (
          <div className="alert alert-danger" role="alert" style={{ marginBottom: '16px' }}>
            {error}
            <button type="button" className="btn-close" onClick={() => setError(null)} style={{ float: 'right' }} />
          </div>
        )}

        <div className="card">
          <div className="card-body">
            <form onSubmit={handleFormSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>Asset *</label>
                    <button type="button" onClick={loadAssets} disabled={loadingAssets} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 8px', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '0', fontSize: '12px', cursor: loadingAssets ? 'not-allowed' : 'pointer', opacity: loadingAssets ? 0.6 : 1, fontFamily: 'var(--font-family)' }}>
                      <RefreshCw size={12} style={{ animation: loadingAssets ? 'spin 1s linear infinite' : 'none' }} />
                      {loadingAssets ? 'Loading...' : 'Refresh'}
                    </button>
                  </div>
                  <select
                    name="assetId"
                    value={formData.assetId}
                    onChange={handleFormChange}
                    required
                    disabled={loadingAssets}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '0', fontSize: '14px', fontFamily: 'var(--font-family)', backgroundColor: loadingAssets ? '#f9fafb' : 'white', cursor: loadingAssets ? 'not-allowed' : 'pointer' }}
                  >
                    <option value="">{loadingAssets ? 'Loading assets...' : `Select an asset (${assets.length} available)`}</option>
                    {assets.map(asset => {
                      const assetId = asset.assetId || asset.assetCode || asset._id || asset.id || '';
                      const assetName = asset.name || asset.assetName || 'Unnamed Asset';
                      const displayText = asset.assetCode || asset.assetId ? `${asset.assetCode || asset.assetId} - ${assetName}` : assetName;
                      return <option key={assetId || asset._id || asset.id} value={assetId}>{displayText}</option>;
                    })}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Type *</label>
                  <select name="type" value={formData.type} onChange={handleFormChange} required style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '0', fontSize: '14px', fontFamily: 'var(--font-family)' }}>
                    <option value="Preventive">Preventive</option>
                    <option value="Corrective">Corrective</option>
                    <option value="Repair">Repair</option>
                    <option value="Emergency">Emergency</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Status *</label>
                  <select name="status" value={formData.status} onChange={handleFormChange} required style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '0', fontSize: '14px', fontFamily: 'var(--font-family)' }}>
                    <option value="Scheduled">Scheduled</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Priority *</label>
                  <select name="priority" value={formData.priority} onChange={handleFormChange} required style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '0', fontSize: '14px', fontFamily: 'var(--font-family)' }}>
                    <option value="High">High</option>
                    <option value="Medium">Medium</option>
                    <option value="Low">Low</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Scheduled Date *</label>
                  <input type="date" name="scheduledDate" value={formData.scheduledDate} onChange={handleFormChange} required style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '0', fontSize: '14px', fontFamily: 'var(--font-family)' }} />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Completed Date</label>
                  <input type="date" name="completedDate" value={formData.completedDate} onChange={handleFormChange} style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '0', fontSize: '14px', fontFamily: 'var(--font-family)' }} />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Cost</label>
                  <input type="number" name="cost" value={formData.cost} onChange={handleFormChange} min="0" step="0.01" style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '0', fontSize: '14px', fontFamily: 'var(--font-family)' }} />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Technician</label>
                  <input type="text" name="technician" value={formData.technician} onChange={handleFormChange} placeholder="e.g., John Smith" style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '0', fontSize: '14px', fontFamily: 'var(--font-family)' }} />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Vendor</label>
                  <input type="text" name="vendor" value={formData.vendor} onChange={handleFormChange} placeholder="e.g., Tech Solutions Inc." style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '0', fontSize: '14px', fontFamily: 'var(--font-family)' }} />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Location</label>
                  <input type="text" name="location" value={formData.location} onChange={handleFormChange} placeholder="e.g., IT Department" style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '0', fontSize: '14px', fontFamily: 'var(--font-family)' }} />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Estimated Duration</label>
                  <input type="text" name="estimatedDuration" value={formData.estimatedDuration} onChange={handleFormChange} placeholder="e.g., 2 hours" style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '0', fontSize: '14px', fontFamily: 'var(--font-family)' }} />
                </div>

                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Description *</label>
                  <input type="text" name="description" value={formData.description} onChange={handleFormChange} placeholder="e.g., Regular maintenance check and software updates" required style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '0', fontSize: '14px', fontFamily: 'var(--font-family)' }} />
                </div>

                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Notes</label>
                  <textarea name="notes" value={formData.notes} onChange={handleFormChange} placeholder="Additional notes or comments..." rows={3} style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '0', fontSize: '14px', fontFamily: 'var(--font-family)', resize: 'vertical' }} />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-outline" onClick={handleCancel} style={{ borderRadius: '0' }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={isLoading} style={{ display: 'flex', alignItems: 'center', gap: '8px', borderRadius: '0', opacity: isLoading ? 0.6 : 1 }}>
                  {isLoading ? (
                    <>
                      <RefreshCw size={16} className="animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      Create Maintenance
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default NewMaintenance;
